import { isUndefined } from "../../../../../lib/html5-software-library/src/ranj/util/TypeUtil";
import TrackString from '../../../analytics/TrackString';
import MapModel from "../../../app/map/model/MapModel";
import MapsConfig from "../../../app/map/model/MapsConfig";
import AbstractCommand from "../../../core/application/AbstractCommand";
import ContextEvent from '../../../core/application/ContextEvent';
import AssetLoadServiceEvent from "../../../core/assetloading/events/AssetLoadServiceEvent";
import SkinKeyRegistry from "../../../core/ui/SkinKeyRegistry";
import LaptopDestroyEvent from "../../laptop/model/LaptopDestroyEvent";
import LaptopSetupEvent from "../../laptop/model/LaptopSetupEvent";
import SmartphoneDestroyEvent from "../../smartphone/model/SmartphoneDestroyEvent";
import SmartphoneSetupEvent from "../../smartphone/model/SmartphoneSetupEvent";
import ScenarioPlayerEvent from "../ScenarioPlayerEvent";
import ScenarioPlayerRegisterEvent from "./ScenarioPlayerRegisterEvent";

/**
* PlayScenarioCommand.js
* 
* Created on 2-okt-2013, 16:08:55
*
* @author Ivo Swartjes for Ranj
**/
export default class PlayScenarioCommand extends AbstractCommand {
    constructor(injector, storyFactory, languagePack, stageManager, playSessionModel, soundManager,
        scenarioLibrary, dispatcher, assetLoadService, assetLocator, revisionToolGateway, variableManager, assetLibrary, mapsConfig, mapModel) {

        super(dispatcher);

        this._currScenarioPlayer;
        this._scenarioToPlay;
        this._storyFactory = storyFactory;
        this._playSessionModel = playSessionModel;
        this._scenarioLibrary = scenarioLibrary;
        this._assetLocator = assetLocator;
        this._dispatcher = dispatcher;
        this._injector = injector;
        this._assetLoadService = assetLoadService;
        this._revisionToolGateway = revisionToolGateway;
        this._variableManager = variableManager;
        this._assetLibrary = assetLibrary;
        this._languagePack = languagePack;
        this._stageManager = stageManager;
        this._soundManager = soundManager;
        this._npcModel = null;

        /** @type {MapsConfig} */
        this._mapsConfig = mapsConfig;
        /** @type {MapModel} */
        this._mapModel = mapModel;

        this._onScenarioLoaded = this.onScenarioLoaded.bind(this);
        this._onScenarioEnd = this.onScenarioEnd.bind(this);

        this.execute = function (event) {

            console.info("Play scenario command");

            // Decide which scenario to play
            if (isUndefined(event.params) || isUndefined(event.params.scenario)) {
                this._scenarioToPlay = this._scenarioLibrary.getDefaultScenario();
            } else {
                this._scenarioToPlay = event.params.scenario;
            }

            // Load scenario assets
            dispatcher.addEventListener(AssetLoadServiceEvent.LOAD_COMPLETE, this._onScenarioLoaded);
            this._assetLoadService.loadGroups(this._scenarioToPlay.id);
            //also add to assetLocator (so DialogEditorGateway can find assets)
            this._assetLocator.addManifest(this._assetLoadService.getManifest(this._scenarioToPlay.id));

            var lazyLoadAssetManifest = this._assetLoadService.getManifest(this._scenarioToPlay.id + "_lazyLoad");
            this._assetLocator.addManifest(lazyLoadAssetManifest);
        }
    }

    onScenarioLoaded(event) {
        this._dispatcher.removeEventListener(AssetLoadServiceEvent.LOAD_COMPLETE, this._onScenarioLoaded);

        // Set the current scenario in the library
        this._scenarioLibrary.setCurrentScenario(this._scenarioToPlay);

        // Start a new scenario play session
        this._playSessionModel.startNewScenarioSession(this._scenarioToPlay.id);
        
        // Analytics: set scenario id as persistent value so all events during scenario are attributed accordingly
        // NB: value is unset when dialog closes/completes
        this.trackPersistentValue(TrackString.PERSISTENT_STORY_ID, this._scenarioToPlay.id);
        this.trackEvent(TrackString.EVENT_SCENARIO_START);

        // Create a new scenario player
        //	NB: scenarioPlayer is not a singleton
        this._currScenarioPlayer = this._injector.getValue('scenarioPlayer');

        // Create story
        this._scenarioToPlay.story = this._storyFactory.getStory(this._scenarioToPlay.storyID);

        // Initialize scenario 
        // TODO: from story memento, if any; else start with clear variable manager
        this._variableManager.reset();
        this._currScenarioPlayer.init(this._scenarioToPlay, null);

        var sprEvent = new ScenarioPlayerRegisterEvent(ScenarioPlayerRegisterEvent.REGISTER, this._currScenarioPlayer);
        this._dispatcher.dispatch(sprEvent.type, sprEvent);

        // TODO: restore story variables (if any)

        // Set up smartphone
        var smartphoneSetupEvent = new SmartphoneSetupEvent(SmartphoneSetupEvent.PERFORM);
        this._dispatcher.dispatch(smartphoneSetupEvent.type, smartphoneSetupEvent);

        // Set up laptop
        var laptopSetupEvent = new LaptopSetupEvent(LaptopSetupEvent.PERFORM);
        this._dispatcher.dispatch(laptopSetupEvent.type, laptopSetupEvent);

        // Start the scenario!
        this._startTime = Date.now();
        this._currScenarioPlayer.eventDispatcher.addEventListener(ScenarioPlayerEvent.SCENARIO_END, this._onScenarioEnd);
        this._currScenarioPlayer.play();

        // TEMP: init map model with first map
        const firstMap = this._mapsConfig.maps[0];
        this._mapModel.setCurrentMap(firstMap);

        // Show hub
        this.openScreen(SkinKeyRegistry.HUB_SCREEN_SKIN);
    }

    onScenarioEnd(event) {
        var sprEvent = new ScenarioPlayerRegisterEvent(ScenarioPlayerRegisterEvent.DEREGISTER, this._currScenarioPlayer);
        this._dispatcher.dispatch(sprEvent.type, sprEvent);

        // Analytics: tracks scenario end
        var duration = Date.now() - this._startTime;
        this.trackEvent(TrackString.EVENT_SCENARIO_END, TrackString.createScenarioEndParams(duration));
        
        // Analytics: unset scenario id from persistent value 
        this.trackPersistentValue(TrackString.PERSISTENT_STORY_ID, null);
        
        this._dispatcher.removeEventListener(AssetLoadServiceEvent.LOAD_COMPLETE, this._onScenarioLoaded);
        this._currScenarioPlayer.eventDispatcher.removeEventListener(ScenarioPlayerEvent.SCENARIO_END, this._onScenarioEnd);
        this._currScenarioPlayer.stop();
        this._currScenarioPlayer = null;

        this._injector = null;
        this._scenarioToPlay = null;
        this._storyFactory = null;
        this._playSessionModel = null;
        this._revisionToolGateway = null;

        this._onScenarioLoaded = null;
        this._onScenarioEnd = null;

        // Destroy Smartphone
        this._dispatcher.dispatch(SmartphoneDestroyEvent.PERFORM);

        // Destroy Laptop
        this._dispatcher.dispatch(LaptopDestroyEvent.PERFORM);

        // TODO - Dispatch event to continue to e.g. a results/score screen here.
        // ...

        // For testing - restart the game!
        var contextEvent = new ContextEvent(ContextEvent.START_GAME);
        this._dispatcher.dispatch(contextEvent.type, { data: contextEvent });
    }
}